import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { clearBusiness, loadBusiness } from '../../actions/business';
import Loader from '../common/Loader';
import NotFound from '../common/NotFound';
import Seal, { formatVerified } from '../common/Seal';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const SEASON_LABEL = {
  summer: 'In season',
  shoulder: 'Shoulder season',
  offseason: 'Offseason',
};

function formatTime(value) {
  if (!value) return null;

  // The API sends a MySQL TIME ("17:30:00"). Date parsing it needs a date part,
  // and building one introduces a timezone the business hours do not have.
  const [hour, minute] = value.split(':');
  const h = Number(hour);
  const suffix = h >= 12 ? 'pm' : 'am';
  const twelve = h % 12 === 0 ? 12 : h % 12;

  return minute === '00' ? `${twelve}${suffix}` : `${twelve}:${minute}${suffix}`;
}

function Hours({ hours }) {
  if (!hours?.length) return null;

  const bySeason = hours.reduce((acc, row) => {
    (acc[row.season] = acc[row.season] || []).push(row);
    return acc;
  }, {});

  return (
    <section className="panel">
      <h2>Hours</h2>
      {Object.entries(bySeason).map(([season, rows]) => (
        <div key={season} className="hours-block">
          <h3>{SEASON_LABEL[season] || season}</h3>
          <dl className="hours">
            {rows.map((row) => (
              <div key={row.id} className="hours-row">
                <dt>{DAYS[row.day_of_week]}</dt>
                <dd>
                  {row.is_closed
                    ? 'Closed'
                    : `${formatTime(row.opens_at)} – ${formatTime(row.closes_at)}`}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </section>
  );
}

/**
 * The whole argument for this site in one block, so it sits above the copy
 * rather than in the sidebar with the phone number.
 */
function Verified({ value }) {
  const parts = formatVerified(value);

  return (
    <section className="panel panel-verified">
      <Seal value={value} />
      <div className="verified-copy">
        <span className="verified-when">
          {parts ? `Hours confirmed ${parts.month} ${parts.day}, ${parts.year}` : 'Hours not confirmed yet'}
        </span>
        <p className="verified-why">
          {parts
            ? 'Somebody called. If that is wrong, tell us — the date on this page is the point of the site, and we would rather hear it than be quietly wrong for a month.'
            : 'These hours came from a public source and nobody has checked them. Treat them as a starting point and call ahead.'}
        </p>
      </div>
    </section>
  );
}

export default function BusinessDetail() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const business = useSelector((state) => state.business.item);
  const loading = useSelector((state) => Boolean(state.api.loading.BUSINESS));

  useEffect(() => {
    dispatch(loadBusiness(slug));

    // Without this the previous listing stays on screen while the next one
    // loads, so a click from one card to another shows the old business under
    // the new URL for as long as the request takes.
    return () => dispatch(clearBusiness());
  }, [dispatch, slug]);

  if (loading || business === undefined) return <Loader full label="Loading listing" />;
  if (business === null) return <NotFound />;

  return (
    <article className="wrap pad listing">
      <nav className="crumbs">
        <Link to="/directory">Directory</Link>
        {business.category && (
          <>
            <span aria-hidden="true"> / </span>
            <Link to={`/directory?category_slug=${business.category.slug}`}>
              {business.category.name}
            </Link>
          </>
        )}
      </nav>

      <header className="listing-head">
        <h1>{business.name}</h1>
        {business.tagline && <p className="lede">{business.tagline}</p>}
        <p className="muted">
          {business.category?.name}
          {business.town?.name ? ` · ${business.town.name}` : ''}
        </p>
      </header>

      {business.images?.length > 0 && (
        <div className="gallery">
          {business.images.map((image) => (
            <figure key={image.id}>
              <img src={image.url} alt={image.alt || business.name} loading="lazy" />
              {image.credit && <figcaption>{image.credit}</figcaption>}
            </figure>
          ))}
        </div>
      )}

      <div className="listing-body">
        <div className="listing-main">
          <Verified value={business.last_verified_at} />

          {business.description && (
            <section className="panel">
              <h2>About</h2>
              <p>{business.description}</p>
            </section>
          )}

          <Hours hours={business.hours} />
        </div>

        <aside className="listing-side">
          <section className="panel">
            <h2>Find it</h2>
            <ul className="facts">
              {business.address_line1 && (
                <li>
                  {business.address_line1}
                  {business.address_line2 ? `, ${business.address_line2}` : ''}
                  <br />
                  {business.town?.name} NJ {business.zip}
                </li>
              )}
              {business.phone && (
                <li>
                  <a href={`tel:${business.phone.replace(/[^\d+]/g, '')}`}>{business.phone}</a>
                </li>
              )}
              {business.website && (
                <li>
                  <a href={business.website} target="_blank" rel="noreferrer noopener">
                    Website
                  </a>
                </li>
              )}
              {business.instagram && (
                <li>
                  <a
                    href={`https://instagram.com/${business.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    @{business.instagram.replace('@', '')}
                  </a>
                </li>
              )}
            </ul>
          </section>

          {business.tags?.length > 0 && (
            <section className="panel">
              <h2>Good to know</h2>
              <div className="chip-row">
                {business.tags.map((tag) => (
                  <span className="chip" key={tag.id}>
                    {tag.name}
                  </span>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </article>
  );
}
